import React, { useState, useEffect } from 'react';
import { User, Shield, CreditCard, FileText, Upload, X } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface KYCDocumentCollectionProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onComplete: () => void;
}

interface KYCData {
  nationalIdPassport: {
    type: 'national_id' | 'passport';
    number: string;
    issueDate: string;
    expiryDate: string;
    frontImage?: string;
    backImage?: string;
  };
  licenseGovernmentCard: {
    type: 'license' | 'government_card';
    number: string;
    issueDate: string;
    expiryDate: string;
    frontImage?: string;
    backImage?: string;
  };
  taxId: {
    type: 'pan' | 'ssn' | 'tax_id' | 'other';
    number: string;
    issueDate: string;
    expiryDate: string;
  };
}

const KYCDocumentCollection: React.FC<KYCDocumentCollectionProps> = ({
  isOpen,
  onClose,
  userId,
  onComplete
}) => {
  const { user: currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [kycData, setKycData] = useState<KYCData>({
    nationalIdPassport: {
      type: 'national_id',
      number: '',
      issueDate: '',
      expiryDate: ''
    },
    licenseGovernmentCard: {
      type: 'license',
      number: '',
      issueDate: '',
      expiryDate: ''
    },
    taxId: {
      type: 'pan',
      number: '',
      issueDate: '',
      expiryDate: ''
    }
  });

  const [imageFiles, setImageFiles] = useState<{
    nationalIdFront?: File;
    nationalIdBack?: File;
    licenseFront?: File;
    licenseBack?: File;
  }>({});

  const [imagePreview, setImagePreview] = useState<{
    nationalIdFront?: string;
    nationalIdBack?: string;
    licenseFront?: string;
    licenseBack?: string;
  }>({});

  // Debug logging for state changes
  useEffect(() => {
    // Image files state changed
  }, [imageFiles]);

  useEffect(() => {
    // Image preview state changed
  }, [imagePreview]);

  const handleInputChange = (section: keyof KYCData, field: string, value: string) => {
    setKycData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleImageUpload = (type: string, file: File) => {
    if (file) {
      // Processing image file
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file (JPEG, PNG, etc.)');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      // Validate file size (min 1KB)
      if (file.size < 1024) {
        toast.error('Image file seems too small. Please check the file.');
        return;
      }

      // Set the image file first
      setImageFiles(prev => {
        const newFiles = { ...prev, [type]: file };
        return newFiles;
      });
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(prev => ({ ...prev, [type]: e.target?.result as string }));
      };
      reader.onerror = () => {
        toast.error('Failed to read image file. Please try another image.');
      };
      reader.readAsDataURL(file);
      
      toast.success(`${type} image uploaded successfully`);
    } else {
      console.error('No file provided to handleImageUpload');
    }
  };

  const removeImage = (type: string) => {
    setImageFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[type as keyof typeof newFiles];
      return newFiles;
    });
    setImagePreview(prev => {
      const newPreview = { ...prev };
      delete newPreview[type as keyof typeof newPreview];
      return newPreview;
    });
  };

  const uploadImageToFirebase = async (file: File, path: string): Promise<string> => {
    try {
      // Uploading image
      
      if (!storage) {
        throw new Error('Firebase Storage is not initialized');
      }
      
      const storageRef = ref(storage, path);
      
      const snapshot = await uploadBytes(storageRef, file);
      
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error: any) {
      console.error('Error uploading image:', error);
      if (error.code === 'storage/unauthorized') {
        throw new Error('You do not have permission to upload files');
      } else if (error.code === 'storage/quota-exceeded') {
        throw new Error('Storage quota exceeded');
      } else {
        throw new Error(`Failed to upload image: ${error.message}`);
      }
    }
  };

  const validateForm = (): boolean => {
    const { nationalIdPassport, licenseGovernmentCard, taxId } = kycData;
    
    // Validating form
    
    // Validate National ID/Passport - only number is required, dates are optional
    if (!nationalIdPassport.number || nationalIdPassport.number.trim() === '') {
      toast.error('Please enter your National ID/Passport number');
      return false;
    }
    
    // Validate License/Government Card - only number is required, dates are optional
    if (!licenseGovernmentCard.number || licenseGovernmentCard.number.trim() === '') {
      toast.error('Please enter your License/Government Card number');
      return false;
    }
    
    // Validate Tax ID - only number is required, dates are optional
    if (!taxId.number || taxId.number.trim() === '') {
      toast.error('Please enter your Tax ID number');
      return false;
    }

    // Check if required images are uploaded
    
    if (!imageFiles.nationalIdFront || !(imageFiles.nationalIdFront instanceof File)) {
      toast.error('Please upload the front image of your National ID/Passport');
      return false;
    }

    if (!imageFiles.nationalIdBack || !(imageFiles.nationalIdBack instanceof File)) {
      toast.error('Please upload the back image of your National ID/Passport');
      return false;
    }

    // Check if License/Government Card images are uploaded (optional)
    // Note: License/Government Card images are optional, not required
    if (imageFiles.licenseFront && !(imageFiles.licenseFront instanceof File)) {
      toast.error('Invalid license front image file');
      return false;
    }

    if (imageFiles.licenseBack && !(imageFiles.licenseBack instanceof File)) {
      toast.error('Invalid license back image file');
      return false;
    }

    // Validate dates only if they are provided
    if (nationalIdPassport.issueDate && nationalIdPassport.expiryDate) {
      const today = new Date();
      const issueDate = new Date(nationalIdPassport.issueDate);
      const expiryDate = new Date(nationalIdPassport.expiryDate);
      
      if (issueDate > today) {
        toast.error('Issue date cannot be in the future');
        return false;
      }
      
      if (expiryDate <= today) {
        toast.error('Expiry date must be in the future');
        return false;
      }
      
      if (issueDate >= expiryDate) {
        toast.error('Issue date must be before expiry date');
        return false;
      }
    }

    // Validate License/Government Card dates if provided
    if (licenseGovernmentCard.issueDate && licenseGovernmentCard.expiryDate) {
      const today = new Date();
      const issueDate = new Date(licenseGovernmentCard.issueDate);
      const expiryDate = new Date(licenseGovernmentCard.expiryDate);
      
      if (issueDate > today) {
        toast.error('License issue date cannot be in the future');
        return false;
      }
      
      if (expiryDate <= today) {
        toast.error('License expiry date must be in the future');
        return false;
      }
      
      if (issueDate >= expiryDate) {
        toast.error('License issue date must be before expiry date');
        return false;
      }
    }

    // Form validation passed
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Submit button clicked
    
    if (!validateForm()) {
      // Form validation failed
      return;
    }
    
    // Form validation passed, proceeding with submission

    if (!userId) {
      toast.error('User ID is missing. Please try logging in again.');
      return;
    }

    if (!currentUser) {
      toast.error('You must be logged in to submit KYC documents.');
      return;
    }

    setIsLoading(true);

    try {
      // Starting KYC submission

      // Upload images to Firebase Storage
      const imageUrls: { [key: string]: string } = {};
      
      if (imageFiles.nationalIdFront) {
        // Uploading front image
        imageUrls.nationalIdFront = await uploadImageToFirebase(
          imageFiles.nationalIdFront, 
          `kyc/${userId}/nationalId_front_${Date.now()}`
        );
        // Front image uploaded
      }
      
      if (imageFiles.nationalIdBack) {
        // Uploading back image
        imageUrls.nationalIdBack = await uploadImageToFirebase(
          imageFiles.nationalIdBack, 
          `kyc/${userId}/nationalId_back_${Date.now()}`
        );
        // Back image uploaded
      }
      
      if (imageFiles.licenseFront) {
        // Uploading license front image
        imageUrls.licenseFront = await uploadImageToFirebase(
          imageFiles.licenseFront, 
          `kyc/${userId}/license_front_${Date.now()}`
        );
        // License front image uploaded
      }
      
      if (imageFiles.licenseBack) {
        // Uploading license back image
        imageUrls.licenseBack = await uploadImageToFirebase(
          imageFiles.licenseBack, 
          `kyc/${userId}/license_back_${Date.now()}`
        );
        // License back image uploaded
      }

      // Prepare KYC data with image URLs
      const kycDataWithImages = {
        ...kycData,
        nationalIdPassport: {
          ...kycData.nationalIdPassport,
          frontImage: imageUrls.nationalIdFront,
          backImage: imageUrls.nationalIdBack
        },
        licenseGovernmentCard: {
          ...kycData.licenseGovernmentCard,
          frontImage: imageUrls.licenseFront,
          backImage: imageUrls.licenseBack
        }
      };

      // KYC data with images

      // Save KYC data to Firestore
      // Saving to Firestore
      
      if (!db) {
        throw new Error('Firestore is not initialized');
      }
      
      const userRef = doc(db, 'users', userId);
      // console.log('User reference created:', userRef);
      
      // First, try to update just the KYC documents to see if basic update works
      try {
        await updateDoc(userRef, {
          kycDocuments: kycDataWithImages,
          kycSubmittedAt: new Date()
        });
        // console.log('KYC documents saved successfully');
        
        // Now try to update the status
        await updateDoc(userRef, {
          status: 'pending'
        });
        // console.log('User status updated to pending');
      } catch (updateError: any) {
        console.error('Error updating user document:', updateError);
        if (updateError.code === 'firestore/permission-denied') {
          throw new Error('You do not have permission to update your profile');
        } else if (updateError.code === 'firestore/not-found') {
          throw new Error('User document not found');
        } else {
          throw new Error(`Failed to update user document: ${updateError.message}`);
        }
      }

      // console.log('KYC documents saved successfully to Firestore');
      toast.success('KYC documents submitted successfully!');
      onComplete(); // This will show the "Account Under Review" popup
    } catch (error: any) {
      console.error('Error saving KYC documents:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      
      // Handle specific Firebase errors
      if (error.code === 'storage/unauthorized') {
        toast.error('You do not have permission to upload files. Please contact support.');
      } else if (error.code === 'storage/quota-exceeded') {
        toast.error('Storage quota exceeded. Please try again later.');
      } else if (error.code === 'firestore/permission-denied') {
        toast.error('You do not have permission to update your profile. Please contact support.');
      } else {
        toast.error(`Failed to save KYC documents: ${error.message || 'Please try again.'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-8 max-w-6xl w-full max-h-[90vh] overflow-y-auto border border-gray-700 shadow-2xl">
        <div className="flex items-center justify-center mb-6">
          <div className="bg-blue-500/20 p-4 rounded-full">
            <Shield className="h-12 w-12 text-blue-500" />
          </div>
        </div>
        
        <h2 className="text-2xl font-bold text-white text-center mb-2">
          Complete Your KYC Verification
        </h2>
        <p className="text-gray-300 text-center mb-8">
          Please provide the following documents to verify your identity. National ID/Passport images are required, while License/Government Card images are optional. This information is required for account approval.
        </p>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* National ID or Passport Section */}
          <div className="bg-gray-700/30 rounded-lg p-6 border border-gray-600">
            <div className="flex items-center mb-4">
              <User className="h-6 w-6 text-blue-400 mr-3" />
              <h3 className="text-lg font-semibold text-white">National ID or Passport</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Document Type</label>
                <select
                  value={kycData.nationalIdPassport.type}
                  onChange={(e) => handleInputChange('nationalIdPassport', 'type', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-600 text-white rounded border border-gray-500 focus:outline-none focus:border-[#2AB7A6]"
                >
                  <option value="national_id">National ID</option>
                  <option value="passport">Passport</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Document Number</label>
                <input
                  type="text"
                  value={kycData.nationalIdPassport.number}
                  onChange={(e) => handleInputChange('nationalIdPassport', 'number', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-600 text-white rounded border border-gray-500 focus:outline-none focus:border-[#2AB7A6]"
                  placeholder="Enter document number"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Issue Date</label>
                <input
                  type="date"
                  value={kycData.nationalIdPassport.issueDate}
                  onChange={(e) => handleInputChange('nationalIdPassport', 'issueDate', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-600 text-white rounded border border-gray-500 focus:outline-none focus:border-[#2AB7A6]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Expiry Date</label>
                <input
                  type="date"
                  value={kycData.nationalIdPassport.expiryDate}
                  onChange={(e) => handleInputChange('nationalIdPassport', 'expiryDate', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-600 text-white rounded border border-gray-500 focus:outline-none focus:border-[#2AB7A6]"
                />
              </div>
            </div>

            {/* Image Upload Section for National ID/Passport */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Front Image */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Front Image *</label>
                <div className="border-2 border-dashed border-gray-500 rounded-lg p-4 text-center">
                  {imagePreview.nationalIdFront ? (
                    <div className="relative">
                      <img 
                        src={imagePreview.nationalIdFront} 
                        alt="Front Preview" 
                        className="w-full h-32 object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage('nationalIdFront')}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-400 text-sm mb-2">Upload front image</p>
                                             <input
                         type="file"
                         accept="image/*"
                         onChange={(e) => {
                           const file = e.target.files?.[0];
                           if (file) {
                            //  console.log('File selected for nationalIdFront:', file);
                             handleImageUpload('nationalIdFront', file);
                           } else {
                            //  console.log('No file selected for nationalIdFront');
                           }
                         }}
                         className="hidden"
                         id="nationalIdFront"
                       />
                      <label
                        htmlFor="nationalIdFront"
                        className="cursor-pointer bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                      >
                        Choose File
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Back Image */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Back Image *</label>
                <div className="border-2 border-dashed border-gray-500 rounded-lg p-4 text-center">
                  {imagePreview.nationalIdBack ? (
                    <div className="relative">
                      <img 
                        src={imagePreview.nationalIdBack} 
                        alt="Back Preview" 
                        className="w-full h-32 object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage('nationalIdBack')}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-400 text-sm mb-2">Upload back image</p>
                                             <input
                         type="file"
                         accept="image/*"
                         onChange={(e) => {
                           const file = e.target.files?.[0];
                           if (file) {
                            //  console.log('File selected for nationalIdBack:', file);
                             handleImageUpload('nationalIdBack', file);
                           } else {
                            //  console.log('No file selected for nationalIdBack');
                           }
                         }}
                         className="hidden"
                         id="nationalIdBack"
                       />
                      <label
                        htmlFor="nationalIdBack"
                        className="cursor-pointer bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                      >
                        Choose File
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* License/Government Card Section */}
          <div className="bg-gray-700/30 rounded-lg p-6 border border-gray-600">
            <div className="flex items-center mb-4">
              <CreditCard className="h-6 w-6 text-green-400 mr-3" />
              <h3 className="text-lg font-semibold text-white">Driver License or Government Card</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Document Type</label>
                <select
                  value={kycData.licenseGovernmentCard.type}
                  onChange={(e) => handleInputChange('licenseGovernmentCard', 'type', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-600 text-white rounded border border-gray-500 focus:outline-none focus:border-[#2AB7A6]"
                >
                  <option value="license">Driver License</option>
                  <option value="government_card">Government Card</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Document Number</label>
                <input
                  type="text"
                  value={kycData.licenseGovernmentCard.number}
                  onChange={(e) => handleInputChange('licenseGovernmentCard', 'number', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-600 text-white rounded border border-gray-500 focus:outline-none focus:border-[#2AB7A6]"
                  placeholder="Enter document number"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Issue Date</label>
                <input
                  type="date"
                  value={kycData.licenseGovernmentCard.issueDate}
                  onChange={(e) => handleInputChange('licenseGovernmentCard', 'issueDate', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-600 text-white rounded border border-gray-500 focus:outline-none focus:border-[#2AB7A6]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Expiry Date</label>
                <input
                  type="date"
                  value={kycData.licenseGovernmentCard.expiryDate}
                  onChange={(e) => handleInputChange('licenseGovernmentCard', 'expiryDate', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-600 text-white rounded border border-gray-500 focus:outline-none focus:border-[#2AB7A6]"
                />
              </div>
            </div>

            {/* Image Upload Section for License/Card */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Front Image */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Front Image (Optional)</label>
                <div className="border-2 border-dashed border-gray-500 rounded-lg p-4 text-center">
                  {imagePreview.licenseFront ? (
                    <div className="relative">
                      <img 
                        src={imagePreview.licenseFront} 
                        alt="License Front Preview" 
                        className="w-full h-32 object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage('licenseFront')}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-400 text-sm mb-2">Upload front image</p>
                                             <input
                         type="file"
                         accept="image/*"
                         onChange={(e) => {
                           const file = e.target.files?.[0];
                           if (file) {
                            //  console.log('File selected for licenseFront:', file);
                             handleImageUpload('licenseFront', file);
                           } else {
                            //  console.log('No file selected for licenseFront');
                           }
                         }}
                         className="hidden"
                         id="licenseFront"
                       />
                      <label
                        htmlFor="licenseFront"
                        className="cursor-pointer bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                      >
                        Choose File
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Back Image */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Back Image (Optional)</label>
                <div className="border-2 border-dashed border-gray-500 rounded-lg p-4 text-center">
                  {imagePreview.licenseBack ? (
                    <div className="relative">
                      <img 
                        src={imagePreview.licenseBack} 
                        alt="License Back Preview" 
                        className="w-full h-32 object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage('licenseBack')}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-400 text-sm mb-2">Upload back image</p>
                                             <input
                         type="file"
                         accept="image/*"
                         onChange={(e) => {
                           const file = e.target.files?.[0];
                           if (file) {
                            //  console.log('File selected for licenseBack:', file);
                             handleImageUpload('licenseBack', file);
                           } else {
                            //  console.log('No file selected for licenseBack');
                           }
                         }}
                         className="hidden"
                         id="licenseBack"
                       />
                      <label
                        htmlFor="licenseBack"
                        className="cursor-pointer bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                      >
                        Choose File
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tax ID Section */}
          <div className="bg-gray-700/30 rounded-lg p-6 border border-gray-600">
            <div className="flex items-center mb-4">
              <FileText className="h-6 w-6 text-purple-400 mr-3" />
              <h3 className="text-lg font-semibold text-white">Tax Identification Number</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Document Type</label>
                <select
                  value={kycData.taxId.type}
                  onChange={(e) => handleInputChange('taxId', 'type', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-600 text-white rounded border border-gray-500 focus:outline-none focus:border-[#2AB7A6]"
                >
                  <option value="pan">PAN (India)</option>
                  <option value="ssn">SSN (USA)</option>
                  <option value="tax_id">Tax ID (Europe/Australia)</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Document Number</label>
                <input
                  type="text"
                  value={kycData.taxId.number}
                  onChange={(e) => handleInputChange('taxId', 'number', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-600 text-white rounded border border-gray-500 focus:outline-none focus:border-[#2AB7A6]"
                  placeholder="Enter tax ID number"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Issue Date</label>
                <input
                  type="date"
                  value={kycData.taxId.issueDate}
                  onChange={(e) => handleInputChange('taxId', 'issueDate', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-600 text-white rounded border border-gray-500 focus:outline-none focus:border-[#2AB7A6]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Expiry Date</label>
                <input
                  type="date"
                  value={kycData.taxId.expiryDate}
                  onChange={(e) => handleInputChange('taxId', 'expiryDate', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-600 text-white rounded border border-gray-500 focus:outline-none focus:border-[#2AB7A6]"
                />
              </div>
            </div>
          </div>

          {/* Summary Section */}
          <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
            <h4 className="text-sm font-medium text-blue-400 mb-3">Upload Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <span className="text-gray-400">National ID Front:</span>
                <span className={`ml-2 ${imageFiles.nationalIdFront ? 'text-green-400' : 'text-red-400'}`}>
                  {imageFiles.nationalIdFront ? '✓ Uploaded' : '✗ Required'}
                </span>
              </div>
              <div>
                <span className="text-gray-400">National ID Back:</span>
                <span className={`ml-2 ${imageFiles.nationalIdBack ? 'text-green-400' : 'text-red-400'}`}>
                  {imageFiles.nationalIdBack ? '✓ Uploaded' : '✗ Required'}
                </span>
              </div>
              <div>
                <span className="text-gray-400">License Front:</span>
                <span className={`ml-2 ${imageFiles.licenseFront ? 'text-green-400' : 'text-gray-400'}`}>
                  {imageFiles.licenseFront ? '✓ Uploaded' : 'Optional'}
                </span>
              </div>
              <div>
                <span className="text-gray-400">License Back:</span>
                <span className={`ml-2 ${imageFiles.licenseBack ? 'text-green-400' : 'text-gray-400'}`}>
                  {imageFiles.licenseBack ? '✓ Uploaded' : 'Optional'}
                </span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-600">
              <span className="text-gray-400">Required Fields:</span>
              <span className={`ml-2 ${kycData.nationalIdPassport.number && kycData.licenseGovernmentCard.number && kycData.taxId.number ? 'text-green-400' : 'text-red-400'}`}>
                {kycData.nationalIdPassport.number && kycData.licenseGovernmentCard.number && kycData.taxId.number ? '✓ All filled' : '✗ Missing some'}
              </span>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center pt-4 space-x-4">
            <button
              type="button"
              onClick={() => {
                // console.log('Testing validation...');
                const isValid = validateForm();
                // console.log('Validation result:', isValid);
              }}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium"
            >
              Test Validation
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-8 py-3 bg-[#2AB7A6] text-white rounded-lg hover:bg-[#25A395] transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Submitting...' : 'Submit KYC Documents'}
            </button>
          </div>
        </form>

        {/* Close Button */}
        <div className="mt-6 text-center">
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default KYCDocumentCollection;
